import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import * as CookieConsent from 'vanilla-cookieconsent';
import Icon from '../../../Icon';
import JustificationContainer from '../../../JustificationContainer';
import { Body, Heading3, Heading4, Label } from '../../../Typography';
import CloseButton from '../../../CloseButton';
import { enUS, nl } from 'date-fns/locale';
import { format, parseISO } from 'date-fns';
import Button from '~/widget/components/Button';

export const cc_backgroundColor = '#eaeff2';
export const cc_hoverColor = '#d4dae0';
export const cc_textColor = '#5e6266';

export type CookieCategories =
  | 'necessary'
  | 'analytics'
  | 'marketing'
  | 'functional';

export type Props = {
  dataTestId?: string;
  cookiePreferences: CookieConsent.UserPreferences;
  timestamp: string;
  consentId: string;
  onClose: () => void;
  onShowPreferences: () => void;
};

const text = {
  nl: {
    consentId: 'Uw toestemming ID:',
    timestamp: 'Datum van toestemming:',
    heading: 'Cookie-instellingen',
    subheading: 'Uw huidige stand',
    buttonLabel: 'Verander uw toestemming',
    cookiePreferences: {
      analytics: 'Analyse',
      necessary: 'Noodzakelijk',
      functional: 'Functioneel',
      marketing: 'Marketing',
    },
  },
  en: {
    heading: 'Cookie settings',
    subheading: 'Your current state',
    consentId: 'Your consent ID:',
    timestamp: 'Consent date:',
    buttonLabel: 'Change consent',
    cookiePreferences: {
      analytics: 'Analytics',
      necessary: 'Necessary',
      functional: 'Functional',
      marketing: 'Marketing',
    },
  },
};

const CookieSettingsModal: React.FC<Props & { locale: 'nl' | 'en' }> = ({
  cookiePreferences,
  timestamp,
  consentId,
  onClose,
  onShowPreferences,
  locale,
  dataTestId,
}) => {
  const theme = useTheme();

  const formattedDate = (() => {
    try {
      if (!timestamp) return '-';
      const parsed = parseISO(timestamp);
      if (Number.isNaN(parsed.getTime())) return '-';
      return format(parsed, 'd MMM yyyy - HH:mm:ss zzz', {
        locale: locale === 'nl' ? nl : enUS,
      });
    } catch {
      return '-';
    }
  })();

  return (
    <Container
      padding={['base']}
      direction="column"
      dataTestId={dataTestId}
      boxShadow="base"
      border={{ radius: 'base' }}
      role="dialog"
      aria-modal="true"
    >
      <JustificationContainer
        width="100%"
        align="center"
        justification="space-between"
        padding={[null, null, 'base', null]}
      >
        <Heading3 $margin={[null]} color={theme.color('primary')}>
          {text[locale].heading}
        </Heading3>
        <CloseButtonWithBackground onClick={onClose} />
      </JustificationContainer>

      <Divider />
      <Heading4 $margin={[null]} color={theme.color('primary')}>
        {text[locale].subheading}
      </Heading4>
      <JustificationContainer
        padding={['base', null]}
        direction="column"
        gap="xxxs"
      >
        <List>
          {(
            cookiePreferences.acceptedCategories as Array<CookieCategories>
          ).map(category => {
            // COOKIES: Remove this if we added functional cookies
            if (category === 'functional') return null;
            return (
              <ListItem key={category}>
                <StyledIcon
                  name={category === 'necessary' ? 'lock' : 'check'}
                  color={theme.color('primary')}
                />
                {text[locale].cookiePreferences[category]}
              </ListItem>
            );
          })}
        </List>
        <List>
          {(
            cookiePreferences.rejectedCategories as Array<CookieCategories>
          ).map(category => {
            // COOKIES: Remove this if we added functional cookies
            if (category === 'functional') return null;
            return (
              <ListItem key={category}>
                <StyledIcon name="close" />
                {text[locale].cookiePreferences[category]}
              </ListItem>
            );
          })}
        </List>
      </JustificationContainer>
      <Details
        direction="column"
        margin={[null, null, 'base', null]}
        padding={['base']}
        gap="s"
        border={{ radius: 'base' }}
      >
        <JustificationContainer direction="column">
          <Label $margin={[null]} $size="s">
            {text[locale].timestamp}
          </Label>
          <Body $size="s" $margin={[null]}>
            {formattedDate}
          </Body>
        </JustificationContainer>
        <JustificationContainer direction="column">
          <Label $margin={[null]} $size="s">
            {text[locale].consentId}
          </Label>
          <Body $size="s" $margin={[null]}>
            {consentId}
          </Body>
        </JustificationContainer>
      </Details>
      <Button
        onClick={() => {
          onShowPreferences();
          onClose();
        }}
      >
        {text[locale].buttonLabel}
      </Button>
    </Container>
  );
};

const StyledIcon = styled(Icon)(
  ({}) => css`
    display: inline-block;
    width: 1.5em;
    height: 1.5em;

    svg {
      width: 100%;
    }
  `,
);
const ListItem = styled.li(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.space('xs')};
  `,
);

const List = styled.ul(
  ({ theme }) => css`
    list-style-type: none;
    margin: 0;
    padding: 0;
    color: ${cc_textColor};
    display: flex;
    flex-direction: column;
    gap: ${theme.space('xxxs')};
  `,
);

const Divider = styled.div(
  ({ theme }) => css`
    border-bottom: 1px solid ${cc_backgroundColor};
    margin-bottom: ${theme.space('xs')};
    width: 100%;
  `,
);
const Container = styled(JustificationContainer)(
  ({ theme }) => css`
    position: fixed;
    background: white;
    bottom: 90px;
    left: 25px;
    z-index: ${theme.getSystem().zIndex.top};
  `,
);

const Details = styled(JustificationContainer)(
  ({}) => css`
    background: ${cc_backgroundColor};

    label,
    p {
      color: ${cc_textColor};
    }
  `,
);

const CLOSE_BUTTON_SIZE = '40px';
const CloseButtonWithBackground = styled(CloseButton)(
  ({ theme }) => css`
    background: ${cc_backgroundColor};
    border: 1px solid ${cc_backgroundColor};
    border-radius: ${theme.getSystem().border.radius.base};
    height: ${CLOSE_BUTTON_SIZE};
    width: ${CLOSE_BUTTON_SIZE};
    transition: all 0.15s ease;
    color: ${theme.color('primary')};

    svg {
      transform: scale(0.8);
      stroke-width: 1.5;
      margin-top: 7px;
    }

    &:hover {
      background: ${cc_hoverColor};
      border: 1px solid ${cc_hoverColor};
      color: ${theme.color('primary', 'text')};
    }
  `,
);

export default CookieSettingsModal;
